import { NotificationTabId, canSubmitNotificationConfig } from "../../../../../utils/planManager/planConfigurationUtils.js";
import TopBar from "../../../common/TopBar";
import TabBar from "../../../common/TabBar";
import VariablesTab from "./children/VariablesTab";
import NotificationsTab from "./children/NotificationsTab";
import PlanManagerModal from "../../../common/PlanManagerModal";
import { useNotification } from "./useNotification";
import { PLANS_LIST_ROUTE } from "../../../common/constants";
import { UNSAVED_CHANGES_MODAL_COPY } from "./useNotification";
import "./Notification.scss";

/**
 * Plan notification setup screen — presentational shell delegating state to
 * {@link useNotification}.
 *
 * @returns {import('react').JSX.Element}
 */
const PlansNotification = () => {
    const {
        form,
        selectedTabId,
        isWarningModalOpen,
        validationErrors,
        selectedCommsLibrary,
        isPartnership,
        isPcpCopayMissing,
        isRbpSelectedYes,
        networkNames,
        selectedLanguage,
        events,
        hasConfigChange,
        autoPopulated,
        clientPlanDisplay,
        notificationTabs,
        showEventsOffWarning,
        handleFieldChange,
        handleSelectOption,
        handleNetworkSelect,
        handleNetworkOtherChange,
        handlePcpCopaySelect,
        handlePcpCopayOtherChange,
        handleReferralSelect,
        handleReferralCustomChange,
        handleRbpSelect,
        handleRbpLanguageSelect,
        handleRbpLanguageTextChange,
        handleEventToggle,
        handleEoyChange,
        handleTabChange,
        handleBackClick,
        handleStayOnPage,
        handleDiscardChanges,
        handleSubmit,
        isLoading,
        isError,
        loadErrorMessage,
    } = useNotification();

    const showNotificationContent = !isLoading && !isError;

    return (
        <div className="plan-notification-page">
            <TopBar variant="detail">
                <TopBar.Back
                    to={PLANS_LIST_ROUTE}
                    label="Plans"
                    ariaLabel="Back to plans"
                    onClick={handleBackClick}
                />
                <TopBar.Title>Notifications</TopBar.Title>
            </TopBar>

            {isLoading && (
                <p className="plan-notification-page__loading" role="status">
                    Loading notification setup...
                </p>
            )}

            {isError && (
                <p className="plan-notification-page__error" role="alert">
                    {loadErrorMessage}
                </p>
            )}

            {showNotificationContent ? (
                <>
                    <TabBar
                        ariaLabel="Notification setup sections"
                        selectedTabId={selectedTabId}
                        tabs={notificationTabs}
                        trailingLabel={clientPlanDisplay}
                        onTabChange={handleTabChange}
                    />

                    {selectedTabId === NotificationTabId.VARIABLES ? (
                        <VariablesTab
                            form={form}
                            autoPopulated={autoPopulated}
                            isPartnershipPlan={isPartnership}
                            isPcpCopaySelected={isPcpCopayMissing}
                            isRbpYes={isRbpSelectedYes}
                            networkNames={networkNames}
                            validationErrors={validationErrors}
                            onFieldChange={handleFieldChange}
                            onSelectOption={handleSelectOption}
                            onNetworkSelect={handleNetworkSelect}
                            onNetworkOtherChange={handleNetworkOtherChange}
                            onPcpCopaySelect={handlePcpCopaySelect}
                            onPcpCopayOtherChange={handlePcpCopayOtherChange}
                            onReferralSelect={handleReferralSelect}
                            onReferralCustomChange={handleReferralCustomChange}
                            onRbpSelect={handleRbpSelect}
                            onRbpLanguageSelect={handleRbpLanguageSelect}
                            onRbpLanguageTextChange={handleRbpLanguageTextChange}
                        />
                    ) : (
                        <NotificationsTab
                            selectedCommsLibraryTitle={
                                selectedCommsLibrary?.title
                            }
                            selectedLanguage={selectedLanguage}
                            events={events}
                            showEventsOffWarning={showEventsOffWarning}
                            isSubmitDisabled={
                                !canSubmitNotificationConfig(
                                    form,
                                    hasConfigChange,
                                )
                            }
                            onSubmit={handleSubmit}
                            onEventToggle={handleEventToggle}
                            onEoyChange={handleEoyChange}
                        />
                    )}
                </>
            ) : null}

            <PlanManagerModal
                isOpen={isWarningModalOpen}
                title={UNSAVED_CHANGES_MODAL_COPY.TITLE}
                body={UNSAVED_CHANGES_MODAL_COPY.BODY}
                primaryText={UNSAVED_CHANGES_MODAL_COPY.STAY}
                onPrimary={handleStayOnPage}
                secondaryText={UNSAVED_CHANGES_MODAL_COPY.DISCARD}
                onSecondary={handleDiscardChanges}
                onClose={handleStayOnPage}
            />
        </div>
    );
};

export default PlansNotification;
